/**
 * Lightweight modal dialogs sharing one light "Notion-style" look. Each helper
 * builds its DOM on demand, appends it to <body>, and resolves a promise when
 * the user confirms or cancels (via a button, the backdrop, or Escape). These
 * replace the native window.prompt / window.confirm flows.
 */

interface DialogHandle {
  dialog: HTMLDivElement;
  close: () => void;
}

/** Mount an empty centered dialog. `onCancel` fires on backdrop click / Escape.
 *  Exported for the dialogs that live in their own module (the trash view), so
 *  every modal in the app is still the same shell. */
export function openShell(onCancel: () => void): DialogHandle {
  const overlay = document.createElement("div");
  overlay.className = "modal-overlay";
  const dialog = document.createElement("div");
  dialog.className = "dialog";
  overlay.appendChild(dialog);
  document.body.appendChild(overlay);

  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") onCancel();
  };
  document.addEventListener("keydown", onKey);
  overlay.addEventListener("mousedown", (e) => {
    if (e.target === overlay) onCancel();
  });

  return {
    dialog,
    close: () => {
      document.removeEventListener("keydown", onKey);
      overlay.remove();
    },
  };
}

export type NewDocKind = "md" | "canvas";

/** Ask for the name of a new markdown/canvas document. Resolves the trimmed
 *  name, or null if cancelled. */
export function openNewDocDialog(kind: NewDocKind): Promise<string | null> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (v: string | null) => {
      if (settled) return;
      settled = true;
      h.close();
      resolve(v);
    };
    const h = openShell(() => finish(null));

    const title = kind === "md" ? "New markdown" : "New canvas";
    const placeholder = kind === "md" ? "note" : "canvas";
    h.dialog.innerHTML = `
      <h2 class="dialog-title">${title}</h2>
      <input class="dialog-input" type="text" placeholder="${placeholder}" />
      <div class="dialog-actions">
        <button type="button" class="btn-ghost" data-act="cancel">Cancel</button>
        <button type="button" class="btn-primary" data-act="ok">Create</button>
      </div>`;

    const input = h.dialog.querySelector<HTMLInputElement>(".dialog-input")!;
    const submit = () => {
      const v = input.value.trim();
      if (v === "") return;
      finish(v);
    };
    h.dialog.querySelector('[data-act="ok"]')!.addEventListener("click", submit);
    h.dialog.querySelector('[data-act="cancel"]')!.addEventListener("click", () => finish(null));
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") submit();
    });
    input.focus();
  });
}

/** Ask for a folder name. Resolves the trimmed name, or null if cancelled. */
export function openFolderDialog(): Promise<string | null> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (v: string | null) => {
      if (settled) return;
      settled = true;
      h.close();
      resolve(v);
    };
    const h = openShell(() => finish(null));

    h.dialog.innerHTML = `
      <h2 class="dialog-title">New folder</h2>
      <input class="dialog-input" type="text" placeholder="folder" />
      <div class="dialog-actions">
        <button type="button" class="btn-ghost" data-act="cancel">Cancel</button>
        <button type="button" class="btn-primary" data-act="ok">Create</button>
      </div>`;

    const input = h.dialog.querySelector<HTMLInputElement>(".dialog-input")!;
    const submit = () => {
      const v = input.value.trim();
      if (v === "") return;
      finish(v);
    };
    h.dialog.querySelector('[data-act="ok"]')!.addEventListener("click", submit);
    h.dialog.querySelector('[data-act="cancel"]')!.addEventListener("click", () => finish(null));
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") submit();
    });
    input.focus();
  });
}

/** Ask for an image display width in px. Resolves the width, or null if the
 *  user cleared the field (auto) or cancelled. */
export function openWidthDialog(currentWidth: number | null): Promise<number | null | undefined> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (v: number | null | undefined) => {
      if (settled) return;
      settled = true;
      h.close();
      resolve(v);
    };
    const h = openShell(() => finish(undefined));

    h.dialog.innerHTML = `
      <h2 class="dialog-title">Image width</h2>
      <input class="dialog-input" type="number" min="1" placeholder="px" />
      <div class="dialog-actions">
        <button type="button" class="btn-ghost" data-act="cancel">Cancel</button>
        <button type="button" class="btn-primary" data-act="ok">Set</button>
      </div>`;

    const input = h.dialog.querySelector<HTMLInputElement>(".dialog-input")!;
    if (currentWidth) input.value = String(currentWidth);
    const submit = () => {
      const v = input.value.trim();
      finish(v === "" ? null : Number(v) || null);
    };
    h.dialog.querySelector('[data-act="ok"]')!.addEventListener("click", submit);
    h.dialog.querySelector('[data-act="cancel"]')!.addEventListener("click", () => finish(undefined));
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") submit();
    });
    input.focus();
    input.select();
  });
}

function escapeHtml(s: string): string {
  const div = document.createElement("div");
  div.textContent = s;
  return div.innerHTML;
}

export type LinkDialogResult =
  | { action: "save"; text: string; href: string }
  | { action: "remove" }
  | null;

/**
 * La UI de un enlace: su texto y su destino.
 *
 * Es la ÚNICA forma de escribir o cambiar un enlace. En la hoja un enlace es
 * una pieza —se ve como enlace y clickearlo navega—, así que el markdown de
 * abajo (`[texto](destino)`) no se edita a mano en ningún momento: se completa
 * acá, y quien quiera ver el texto crudo tiene el modo crudo.
 *
 * El destino acepta tanto una ruta del vault (se ofrecen las del árbol mientras
 * se escribe) como una URL externa; quien llama traduce lo elegido a la ruta
 * relativa que hay que escribir.
 */
export function openLinkDialog(opts: {
  text: string;
  href: string;
  targets: string[];
  /** Si el enlace ya existe, se ofrece quitarlo. */
  existing: boolean;
}): Promise<LinkDialogResult> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (v: LinkDialogResult) => {
      if (settled) return;
      settled = true;
      h.close();
      resolve(v);
    };
    const h = openShell(() => finish(null));
    const listId = `link-targets-${Math.random().toString(36).slice(2)}`;

    h.dialog.innerHTML = `
      <h2 class="dialog-title">${opts.existing ? "Edit link" : "Link"}</h2>
      <label class="dialog-field">
        <span class="dialog-label">Text</span>
        <input class="dialog-input" type="text" data-f="text" placeholder="what it says" />
      </label>
      <label class="dialog-field">
        <span class="dialog-label">Destination</span>
        <input class="dialog-input" type="text" data-f="href" list="${listId}"
               placeholder="note.md, example.com or https://…" />
      </label>
      <datalist id="${listId}">${opts.targets
        .map((t) => `<option value="${escapeHtml(t)}"></option>`)
        .join("")}</datalist>
      <div class="dialog-actions">
        ${opts.existing ? '<button type="button" class="btn-ghost is-danger" data-act="remove">Remove</button>' : ""}
        <button type="button" class="btn-ghost" data-act="cancel">Cancel</button>
        <button type="button" class="btn-primary" data-act="ok">Save</button>
      </div>`;

    const text = h.dialog.querySelector<HTMLInputElement>('[data-f="text"]')!;
    const href = h.dialog.querySelector<HTMLInputElement>('[data-f="href"]')!;
    text.value = opts.text;
    href.value = opts.href;

    const submit = () => {
      const destino = href.value.trim();
      if (destino === "") return; // un enlace sin destino no es un enlace
      finish({ action: "save", text: text.value.trim(), href: destino });
    };
    h.dialog.querySelector('[data-act="ok"]')!.addEventListener("click", submit);
    h.dialog.querySelector('[data-act="cancel"]')!.addEventListener("click", () => finish(null));
    h.dialog
      .querySelector('[data-act="remove"]')
      ?.addEventListener("click", () => finish({ action: "remove" }));
    for (const input of [text, href]) {
      input.addEventListener("keydown", (e) => {
        if (e.key === "Enter") submit();
      });
    }
    // Con el destino vacío se arranca por ahí; si ya está, por el texto.
    const first = opts.href === "" ? href : text;
    first.focus();
    first.select();
  });
}

export type RenameResult = { action: "rename"; name: string } | null;

/** Ask for a new name. Resolves null if cancelled. */
export function openRenameDialog(name: string): Promise<RenameResult> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (v: RenameResult) => {
      if (settled) return;
      settled = true;
      h.close();
      resolve(v);
    };
    const h = openShell(() => finish(null));

    h.dialog.innerHTML = `
      <h2 class="dialog-title">Rename</h2>
      <input class="dialog-input" type="text" />
      <div class="dialog-actions">
        <button type="button" class="btn-ghost" data-act="cancel">Cancel</button>
        <button type="button" class="btn-primary" data-act="ok">Save</button>
      </div>`;

    const input = h.dialog.querySelector<HTMLInputElement>(".dialog-input")!;
    input.value = name;
    const submit = () => {
      const v = input.value.trim();
      if (v === "") return;
      finish({ action: "rename", name: v });
    };
    h.dialog.querySelector('[data-act="ok"]')!.addEventListener("click", submit);
    h.dialog.querySelector('[data-act="cancel"]')!.addEventListener("click", () => finish(null));
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") submit();
    });
    input.focus();
    input.select();
  });
}

/** Yes/no confirmation. Resolves true only if the user accepts. */
export function confirmDialog(opts: {
  title: string;
  message?: string;
  okLabel?: string;
  danger?: boolean;
}): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (v: boolean) => {
      if (settled) return;
      settled = true;
      h.close();
      resolve(v);
    };
    const h = openShell(() => finish(false));
    const okClass = opts.danger ? "btn-danger" : "btn-primary";

    h.dialog.innerHTML = `
      <h2 class="dialog-title"></h2>
      ${opts.message ? '<p class="dialog-msg"></p>' : ""}
      <div class="dialog-actions">
        <button type="button" class="btn-ghost" data-act="cancel">Cancel</button>
        <button type="button" class="${okClass}" data-act="ok"></button>
      </div>`;

    h.dialog.querySelector(".dialog-title")!.textContent = opts.title;
    if (opts.message) h.dialog.querySelector(".dialog-msg")!.textContent = opts.message;
    const okBtn = h.dialog.querySelector<HTMLButtonElement>('[data-act="ok"]')!;
    okBtn.textContent = opts.okLabel ?? "OK";
    okBtn.addEventListener("click", () => finish(true));
    h.dialog.querySelector('[data-act="cancel"]')!.addEventListener("click", () => finish(false));
    okBtn.focus();
  });
}

/**
 * Pide permiso antes de reescribir los enlaces de otros documentos.
 *
 * Mover un archivo deja enlaces apuntando al vacío, y arreglarlos significa
 * TOCAR archivos que el usuario escribió y de los que no hay copia. Ese
 * trabajo no se hace a espaldas de nadie: acá se muestra exactamente en qué
 * documentos y cuántos destinos, y el usuario decide. Lo que se responde que
 * no queda tal cual estaba —enlaces rotos, pero nada reescrito sin querer.
 */
export function openLinkUpdateDialog(
  files: { path: string; count: number }[],
): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (v: boolean) => {
      if (settled) return;
      settled = true;
      h.close();
      resolve(v);
    };
    const h = openShell(() => finish(false));

    const links = files.reduce((n, f) => n + f.count, 0);
    const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

    h.dialog.innerHTML = `
      <h2 class="dialog-title">Update links?</h2>
      <p class="dialog-msg"></p>
      <ul class="dialog-list"></ul>
      <div class="dialog-actions">
        <button type="button" class="btn-ghost" data-act="cancel">Leave as is</button>
        <button type="button" class="btn-primary" data-act="ok">Update links</button>
      </div>`;

    h.dialog.querySelector(".dialog-msg")!.textContent =
      `${links} ${plural(links, "link points", "links point")} at what you just moved, ` +
      `in ${files.length} ${plural(files.length, "document", "documents")}.`;

    const list = h.dialog.querySelector(".dialog-list")!;
    for (const f of files) {
      const li = document.createElement("li");
      const name = document.createElement("span");
      name.className = "dialog-list-name";
      name.textContent = f.path;
      const count = document.createElement("span");
      count.className = "dialog-list-count";
      count.textContent = String(f.count);
      li.append(name, count);
      list.appendChild(li);
    }

    const okBtn = h.dialog.querySelector<HTMLButtonElement>('[data-act="ok"]')!;
    okBtn.addEventListener("click", () => finish(true));
    h.dialog.querySelector('[data-act="cancel"]')!.addEventListener("click", () => finish(false));
    okBtn.focus();
  });
}

export interface ContextMenuItem {
  id: string;
  label: string;
  danger?: boolean;
}

export interface OptionsPopupItem {
  id: string;
  label: string;
  icon: string;
  danger?: boolean;
}

/**
 * A small popup menu anchored at a screen position (editor-style right-click /
 * "⋯" menu), as opposed to the centered modal dialogs above. Resolves the
 * chosen item's id, or null if dismissed via outside click / Escape.
 */
export function openContextMenu(
  x: number,
  y: number,
  items: ContextMenuItem[],
  /** `"dark"`: placa oscura con texto blanco (el menú del lienzo). */
  variant?: "dark",
): Promise<string | null> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (v: string | null) => {
      if (settled) return;
      settled = true;
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("keydown", onKey);
      menu.remove();
      resolve(v);
    };

    const menu = document.createElement("div");
    menu.className = variant === "dark" ? "ctx-menu dark" : "ctx-menu";
    menu.style.left = `${x}px`;
    menu.style.top = `${y}px`;
    for (const item of items) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "ctx-menu-item" + (item.danger ? " danger" : "");
      btn.textContent = item.label;
      btn.addEventListener("click", () => finish(item.id));
      menu.appendChild(btn);
    }
    document.body.appendChild(menu);

    // Nudge back on-screen if the anchor point was near an edge.
    const rect = menu.getBoundingClientRect();
    const overflowX = rect.right - window.innerWidth;
    const overflowY = rect.bottom - window.innerHeight;
    if (overflowX > 0) menu.style.left = `${Math.max(4, x - overflowX)}px`;
    if (overflowY > 0) menu.style.top = `${Math.max(4, y - overflowY)}px`;

    const onOutside = (e: MouseEvent) => {
      if (!menu.contains(e.target as Node)) finish(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") finish(null);
    };
    // Deferred so the click/contextmenu event that opened the menu doesn't
    // immediately trigger the outside-click listener.
    setTimeout(() => {
      document.addEventListener("mousedown", onOutside);
      document.addEventListener("keydown", onKey);
    }, 0);
  });
}

/**
 * A small dark popup anchored at (x, y), positioned next to the "⋯" button.
 * Shows a list of actions with icons. Returns the selected item's id, or
 * null if dismissed via outside click / Escape.
 */
export function openOptionsPopup(
  x: number,
  y: number,
  items: OptionsPopupItem[],
): Promise<string | null> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (v: string | null) => {
      if (settled) return;
      settled = true;
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("keydown", onKey);
      popup.remove();
      resolve(v);
    };

    const popup = document.createElement("div");
    popup.className = "options-popup";
    popup.setAttribute("role", "menu");
    popup.style.left = `${x}px`;
    popup.style.top = `${y}px`;
    popup.innerHTML = items
      .map(
        (item, i) =>
          // Divider before the destructive group, editor-menu style.
          (item.danger && i > 0 && !items[i - 1].danger
            ? '<div class="options-popup-sep"></div>'
            : "") +
          `<button type="button" role="menuitem" class="options-popup-item${item.danger ? " danger" : ""}" data-id="${item.id}">
            <span class="options-popup-icon">${item.icon}</span>
            <span>${escapeHtml(item.label)}</span>
          </button>`,
      )
      .join("");
    document.body.appendChild(popup);

    // Nudge back on-screen if near an edge.
    const rect = popup.getBoundingClientRect();
    const overflowX = rect.right - window.innerWidth;
    const overflowY = rect.bottom - window.innerHeight;
    if (overflowX > 0) popup.style.left = `${Math.max(4, x - overflowX)}px`;
    if (overflowY > 0) popup.style.top = `${Math.max(4, y - overflowY)}px`;

    popup.querySelectorAll(".options-popup-item").forEach((btn) => {
      btn.addEventListener("click", () => finish((btn as HTMLElement).dataset.id!));
    });

    const onOutside = (e: MouseEvent) => {
      if (!popup.contains(e.target as Node)) finish(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") finish(null);
    };
    setTimeout(() => {
      document.addEventListener("mousedown", onOutside);
      document.addEventListener("keydown", onKey);
    }, 0);
  });
}
